// Considered: StatusFactorPanel history list — rejected because this is a distinct concern
// (check-in history + composer) from the summary shell; extracted to keep FactorSummaryMolecule
// type-agnostic. See spec Reuse Audit.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { StatusValue } from 'types/spherePoints';
import StatusBadgeAtom from '../../atoms/status-badge/StatusBadgeAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import SphereNameInline from '../../atoms/SphereNameInline/SphereNameInline';
import CheckInFormContent from '../StatusCheckInDialog/CheckInFormContent';
import { useFactorCheckInHistory } from '../../../hooks/useFactorCheckInHistory';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { deriveDelta } from '../../../utils/statusDelta';
import { formatRelativeTime } from '../../../utils/formatRelativeTime';
import type { CheckInHistoryItem } from '../../../types/checkIns';
import './ManualFactorSourceSection.scss';

interface ManualFactorSourceSectionProps {
  factorId: string;
  sphereId: string;
  hasEditPermission: boolean;
  /** Factor lifecycle mode; hide + New check-in when paused per EAINT-739 */
  mode?: string;
  /**
   * EAINT-842 Board 4 parity: when set, the Latest check-in card renders a
   * "next check-in due <when>" line under the quoted note. Optional because
   * the wiring lives upstream (parent needs a schedule resolver or a
   * `dueAt` value on the factor read); passing `undefined` simply omits the
   * line and matches the pre-parity render.
   */
  nextDueAt?: string;
  onCheckInSubmitted?: () => void;
  /**
   * EAINT-916: The RESULT row (status chip + velocity) that the parent
   * component owns. When provided, it renders INSIDE this component's
   * top card, above the latest check-in narrative — grouping RESULT +
   * narrative + quote in one panel per handoff Board 1. The CHECK-IN
   * HISTORY (header + list) sits in a second panel below.
   *
   * When null/undefined (e.g. non-manual factor types) this component
   * behaves as before and only owns its own content.
   */
  resultSlot?: React.ReactNode;
}

const ManualFactorSourceSection: React.FC<ManualFactorSourceSectionProps> = ({
  factorId,
  sphereId,
  hasEditPermission,
  mode,
  nextDueAt,
  onCheckInSubmitted,
  resultSlot,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { checkIns, loading, error, refresh } = useFactorCheckInHistory(factorId, sphereId, {
    limit: 20,
  });
  const [showComposer, setShowComposer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isPaused = mode === 'paused';

  async function handleSubmit(statusValue: StatusValue, comment?: string) {
    if (!apiClients?.graphClient) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiClients.graphClient.post(
        `/sphere/${sphereId}/factors/${factorId}/checkin`,
        { statusValue, comment }
      );
      setShowComposer(false);
      refresh();
      onCheckInSubmitted?.();
    } catch {
      setSubmitError(t('statusFactors.summary.checkInSubmitError'));
    } finally {
      setSubmitting(false);
    }
  }

  function renderDeltaChip(item: CheckInHistoryItem) {
    const direction = deriveDelta(item.statusValue, item.previousStatus);
    if (!direction || direction === 'same') return null;

    // EAINT-842 Board 4 parity: inline glyph ▼ / ▲ with the label text on
    // the same visual chip. PrimeIcons rendered with icon-only baselines
    // that broke alignment against the label; a Unicode glyph is
    // typographically consistent with the surrounding text weight and
    // sizes with the label at any zoom.
    const glyph = direction === 'improved' ? '▲' : '▼';
    const previousLabel = item.previousStatus
      ? t(`statusVocabulary.${item.previousStatus}`)
      : '';

    return (
      <span
        className={`manual-factor-source-section__delta manual-factor-source-section__delta--${direction}`}
        data-testid={`delta-chip-${item.id}`}
        data-direction={direction}
      >
        <span className="manual-factor-source-section__delta-glyph" aria-hidden="true">
          {glyph}
        </span>
        {t('statusFactors.summary.deltaFromPrevious', { status: previousLabel })}
      </span>
    );
  }

  const latestCheckIn = checkIns[0];

  return (
    <div className="manual-factor-source-section">
      {/* EAINT-916 top card: groups the RESULT row (from parent via
          `resultSlot`) + the latest check-in narrative / quote / meta.
          Per handoff Board 1 these are one visually distinct panel. */}
      <div className="manual-factor-source-section__top-card">
        {resultSlot}

        {/* EAINT-842 Board 1 parity, refined by EAINT-916: latest check-in
            block composes as a narrative sentence with inline
            "(<author> · <date>)" attribution + second sentence about
            perspectives → italic quoted note → single meta line combining
            author + date + "next check-in due <when>". Author uses
            `SphereNameInline` (plain text, no avatar chip) per the Miro
            feedback that we shouldn't be showing avatars in this surface. */}
        {!loading && !error && latestCheckIn && (
        <section
          className="manual-factor-source-section__latest"
          data-testid="latest-checkin-quote"
        >
          <p className="manual-factor-source-section__latest-narrative">
            {t('statusFactors.summary.latestNarrativeIntro')}
            {' ('}
            {latestCheckIn.submittedBy && (
              <>
                <SphereNameInline
                  sphereId={latestCheckIn.submittedBy}
                  className="manual-factor-source-section__inline-author"
                />
                {' · '}
              </>
            )}
            {formatRelativeTime(latestCheckIn.submittedAt, t)}
            {'). '}
            {t('statusFactors.summary.latestNarrativePerspectivesNote')}
          </p>
          {latestCheckIn.comment && (
            <blockquote className="manual-factor-source-section__latest-comment">
              {latestCheckIn.comment}
            </blockquote>
          )}
          <div className="manual-factor-source-section__latest-meta">
            {latestCheckIn.submittedBy && (
              <>
                <SphereNameInline
                  sphereId={latestCheckIn.submittedBy}
                  className="manual-factor-source-section__author"
                />
                <span
                  className="manual-factor-source-section__meta-sep"
                  aria-hidden="true"
                >
                  ·
                </span>
              </>
            )}
            <span className="manual-factor-source-section__time">
              {formatRelativeTime(latestCheckIn.submittedAt, t)}
            </span>
            {nextDueAt && (
              <>
                <span
                  className="manual-factor-source-section__meta-sep"
                  aria-hidden="true"
                >
                  ·
                </span>
                <span
                  className="manual-factor-source-section__next-due"
                  data-testid="next-check-in-due"
                >
                  {t('statusFactors.summary.nextCheckInDue', {
                    when: formatRelativeTime(nextDueAt, t),
                  })}
                </span>
              </>
            )}
          </div>
        </section>
      )}
      </div>

      {/* EAINT-916 history card: header + list + composer + states.
          Sits below the top card as its own visually distinct panel. */}
      <div className="manual-factor-source-section__history-card">
      {/* EAINT-842 Board 1 parity: header shows the eyebrow label on the
          left; on the right, an outlined "N check-ins" pill + a text
          "+ New check-in" button. The count chip is back — earlier removal
          was based on a misreading of the mockup. */}
      <div className="manual-factor-source-section__header">
        <span className="manual-factor-source-section__header-label">
          {t('statusFactors.summary.checkInHistoryHeader')}
        </span>
        <div className="manual-factor-source-section__header-actions">
          {!loading && (
            <span
              className="manual-factor-source-section__count"
              data-testid="checkin-count-chip"
            >
              {t('statusFactors.summary.checkInCount', { count: checkIns.length })}
            </span>
          )}
          {hasEditPermission && !isPaused && (
            <ButtonAtom
              type="button"
              variant="text"
              label={t('statusFactors.summary.newCheckInButton')}
              className="manual-factor-source-section__new-btn"
              data-testid="new-checkin-button"
              onClick={() => setShowComposer((v) => !v)}
            />
          )}
        </div>
      </div>

      {showComposer && (
        <CheckInFormContent
          isSubmitting={submitting}
          error={submitError}
          onSubmit={handleSubmit}
          onCancel={() => setShowComposer(false)}
        />
      )}

      {loading && (
        <p className="manual-factor-source-section__loading">
          {t('statusFactors.loading')}
        </p>
      )}

      {!loading && error && (
        <p className="manual-factor-source-section__error" role="alert">
          {t('statusFactors.summary.historyLoadError')}
        </p>
      )}

      {!loading && !error && checkIns.length === 0 && (
        <p className="manual-factor-source-section__empty">
          {t('statusFactors.summary.noHistoryYet')}
        </p>
      )}

      {/* EAINT-916 (Miro-feedback rework of the Board 4 two-line row).
          Handoff Board 1 template:
            Top line     : status dot · status label · delta chip · date (right)
            Middle line  : italic quoted comment (only when present)
            Bottom line  : plain author name (small, secondary)
          Avatar chip dropped — the row is denser and reads as an
          activity-log entry, not a chat message. */}
      <ul className="manual-factor-source-section__list">
        {checkIns.map((item) => (
          <li
            key={item.id}
            className="manual-factor-source-section__row"
            data-testid={`checkin-history-row-${item.id}`}
          >
            <div className="manual-factor-source-section__row-top">
              <StatusBadgeAtom status={item.statusValue} variant="dot" />
              <span
                className={`manual-factor-source-section__status-label manual-factor-source-section__status-label--${item.statusValue}`}
                data-status={item.statusValue}
              >
                {t(`statusVocabulary.${item.statusValue}`)}
              </span>
              {renderDeltaChip(item)}
              <span
                className="manual-factor-source-section__time"
                title={new Date(item.submittedAt).toLocaleString()}
              >
                {formatRelativeTime(item.submittedAt, t)}
              </span>
            </div>
            {item.comment && (
              <blockquote className="manual-factor-source-section__comment">
                {item.comment}
              </blockquote>
            )}
            {item.submittedBy && (
              <SphereNameInline
                sphereId={item.submittedBy}
                className="manual-factor-source-section__author"
              />
            )}
          </li>
        ))}
      </ul>
      </div>
    </div>
  );
};

export default ManualFactorSourceSection;
